/**
 * PromptEditorModal Component (SWRM-103)
 * 
 * Modal for editing a single prompt with:
 * - Large textarea for prompt content
 * - Variables reference panel
 * - Warning for unknown variables
 * - Reset to Default button
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, RotateCcw } from 'lucide-react';
import { PromptData } from '../../types/pipeline';
import { parseOutputDeclarations, validateDynamicOutputs, DynamicOutput } from '../../utils/dynamicOutputs';

interface PromptEditorModalProps {
  isOpen: boolean;
  promptName: string;
  promptData: PromptData;
  currentContent: string;
  processorName?: string; // Processor type (e.g., 'oneshot_document', 'vector')
  standardOutputKeys?: string[]; // Standard outputs from processor metadata
  onSave: (content: string) => void;
  onClose: () => void;
  onOutputsChange?: (outputs: DynamicOutput[]) => void; // Callback when dynamic outputs change
}

export const PromptEditorModal: React.FC<PromptEditorModalProps> = ({
  isOpen,
  promptName,
  promptData,
  currentContent,
  processorName,
  standardOutputKeys = [],
  onSave,
  onClose,
  onOutputsChange,
}) => {
  const [content, setContent] = useState(currentContent);
  const [unknownVariables, setUnknownVariables] = useState<string[]>([]);
  const [missingVariables, setMissingVariables] = useState<string[]>([]);
  const [dynamicOutputs, setDynamicOutputs] = useState<DynamicOutput[]>([]);
  const [outputConflicts, setOutputConflicts] = useState<DynamicOutput[]>([]);
  const [unsupportedReferences, setUnsupportedReferences] = useState<string[]>([]); // [[]] syntax in OneShot
  const [multipleOutputsError, setMultipleOutputsError] = useState(false); // Multiple <<>> in OneShot
  const [hasChanges, setHasChanges] = useState(false);
  const [mouseDownInside, setMouseDownInside] = useState(false);

  // Validate variables in the prompt (bidirectional)
  // Only validate for user prompts - system prompts don't need input variables
  const validateVariables = useCallback((promptContent: string) => {
    // Skip validation for system prompts
    if (promptData.type === 'system') {
      setUnknownVariables([]);
      setMissingVariables([]);
      setDynamicOutputs([]);
      setOutputConflicts([]);
      return;
    }

    // Match both {{variable}} (content) and [[variable]] (reference) syntax
    const contentPattern = /\{\{(\w+)\}\}/g;
    const referencePattern = /\[\[(\w+)\]\]/g;
    const foundVariables = new Set<string>();
    let match;

    // Find {{variable}} patterns
    while ((match = contentPattern.exec(promptContent)) !== null) {
      foundVariables.add(match[1]);
    }

    // Find [[variable]] patterns
    while ((match = referencePattern.exec(promptContent)) !== null) {
      foundVariables.add(match[1]);
    }

    const knownVariables = new Set(promptData.variables);
    const optionalVariables = new Set(promptData.optional_variables || []);

    // Check for unknown variables (user added variables that aren't expected)
    const unknown = Array.from(foundVariables).filter(v => !knownVariables.has(v));
    setUnknownVariables(unknown);

    // Check for missing required variables (expected variables not found in prompt)
    // Exclude optional variables from missing warnings
    const missing = promptData.variables.filter(v => 
      !foundVariables.has(v) && !optionalVariables.has(v)
    );
    setMissingVariables(missing);
    
    // Parse and validate dynamic outputs (<<variable>> syntax)
    const parsedOutputs = parseOutputDeclarations(promptContent);
    setDynamicOutputs(parsedOutputs);
    
    // Validate dynamic outputs against standard outputs
    const validation = validateDynamicOutputs(parsedOutputs, standardOutputKeys);
    setOutputConflicts(validation.conflicts);
    
    // Check for [[reference]] syntax in OneShot processors (not supported)
    const isOneShotProcessor = processorName === 'oneshot_document';
    if (isOneShotProcessor) {
      const referenceMatches: string[] = [];
      const refPattern = /\[\[(\w+)\]\]/g;
      let refMatch;
      while ((refMatch = refPattern.exec(promptContent)) !== null) {
        referenceMatches.push(refMatch[1]);
      }
      setUnsupportedReferences(referenceMatches);
      
      // Validate that OneShot has at most ONE dynamic output
      // (OneShot can only produce one artifact, which can be renamed)
      setMultipleOutputsError(parsedOutputs.length > 1);
    } else {
      setUnsupportedReferences([]);
      setMultipleOutputsError(false);
    }
  }, [promptData.variables, promptData.optional_variables, promptData.type, standardOutputKeys, processorName]);

  // Reset state when modal opens or prompt changes
  // Note: currentContent is intentionally NOT in dependencies to avoid infinite loops
  // We only want to reset when the modal opens or switches to a different prompt
  useEffect(() => {
    setContent(currentContent);
    setHasChanges(false);
    validateVariables(currentContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, promptName]);

  // Notify parent of dynamic outputs changes (separate effect to avoid infinite loop)
  useEffect(() => {
    if (onOutputsChange) {
      onOutputsChange(dynamicOutputs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicOutputs]); // Only re-run when dynamicOutputs changes, not when callback changes

  const handleContentChange = (newContent: string) => {
    setContent(newContent);
    setHasChanges(newContent !== currentContent);
    validateVariables(newContent);
  };

  const handleSave = () => {
    onSave(content);
    setHasChanges(false);
  };

  const handleResetToDefault = () => {
    setContent(promptData.content);
    setHasChanges(promptData.content !== currentContent);
    validateVariables(promptData.content);
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?'
      );
      if (!confirmed) return;
    }
    onClose();
  };

  const handleBackdropClick = () => {
    // Only close if the mouse was pressed down outside the modal
    // This prevents closing when dragging from inside to outside
    if (!mouseDownInside) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="prompt-editor-modal-wrapper fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50"
      onMouseDown={() => setMouseDownInside(false)}
      onClick={handleBackdropClick}
    >
      <div 
        className="prompt-editor-modal-container bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        onMouseDown={(e) => {
          setMouseDownInside(true);
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Edit Prompt
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {promptName}
              <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                {promptData.type}
              </span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Description */}
          {promptData.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {promptData.description}
            </p>
          )}

          {/* Variables Reference */}
          {promptData.variables.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                Available Variables
              </h3>
              <div className="flex flex-wrap gap-2">
                {promptData.variables.map((variable) => (
                  <code
                    key={variable}
                    className="text-xs bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 px-2 py-1 rounded text-blue-700 dark:text-blue-300"
                  >
                    {`{{${variable}}}`}
                  </code>
                ))}
              </div>
            </div>
          )}

          {/* Missing Variables Warning */}
          {missingVariables.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  Unused Connected Inputs
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  The following inputs are connected but not used in your prompt:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {missingVariables.map((variable) => (
                    <code
                      key={variable}
                      className="text-xs bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 px-2 py-1 rounded text-amber-700 dark:text-amber-300"
                    >
                      {`{{${variable}}}`}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Unknown Variables Warning */}
          {unknownVariables.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  Unknown Variables
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  The following variables are not recognized and may not be substituted:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {unknownVariables.map((variable) => {
                    // Check which format was used in the prompt
                    const hasContentSyntax = content.includes(`{{${variable}}}`);
                    const hasRefSyntax = content.includes(`[[${variable}]]`);
                    
                    return (
                      <code
                        key={variable}
                        className="text-xs bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 px-2 py-1 rounded text-amber-700 dark:text-amber-300"
                      >
                        {hasContentSyntax && `{{${variable}}}`}
                        {hasRefSyntax && !hasContentSyntax && `[[${variable}]]`}
                        {!hasContentSyntax && !hasRefSyntax && `{{${variable}}}`}
                      </code>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Unsupported Reference Syntax Error (OneShot only) */}
          {unsupportedReferences.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-900 dark:text-red-200">
                  Unsupported Syntax: [[reference]] in OneShot Processor
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  OneShot processors cannot resolve artifact references. Use <code className="px-1 py-0.5 bg-red-100 dark:bg-red-900 rounded">{`{{variable}}`}</code> for full content insertion:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {unsupportedReferences.map((variable) => (
                    <div key={variable} className="flex items-center gap-2">
                      <code className="text-xs bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 px-2 py-1 rounded text-red-700 dark:text-red-300 line-through">
                        {`[[${variable}]]`}
                      </code>
                      <span className="text-red-600 dark:text-red-400">→</span>
                      <code className="text-xs bg-green-50 dark:bg-green-900/30 border border-green-300 dark:border-green-700 px-2 py-1 rounded text-green-700 dark:text-green-300">
                        {`{{${variable}}}`}
                      </code>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Multiple Dynamic Outputs Error (OneShot only) */}
          {multipleOutputsError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-900 dark:text-red-200">
                  Multiple Dynamic Outputs in OneShot Processor
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  OneShot processors can only produce one artifact. Use at most ONE <code className="px-1 py-0.5 bg-red-100 dark:bg-red-900 rounded">{`<<output>>`}</code> declaration to rename the default "output" artifact.
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {dynamicOutputs.map((output) => (
                    <code
                      key={output.name}
                      className="text-xs bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 px-2 py-1 rounded text-red-700 dark:text-red-300"
                    >
                      {`<<${output.name}>>`}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Dynamic Outputs Detected */}
          {dynamicOutputs.length > 0 && !multipleOutputsError && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded p-3 flex items-start gap-2">
              <div className="text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0">⚡</div>
              <div>
                <h3 className="text-sm font-medium text-purple-900 dark:text-purple-200">
                  {processorName === 'oneshot_document' 
                    ? 'Dynamic Output Renaming' 
                    : 'Dynamic Outputs Detected'}
                </h3>
                <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                  {processorName === 'oneshot_document'
                    ? 'The default "output" artifact will be renamed to:'
                    : 'These outputs will be created as connectable outputs on this processor:'}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {dynamicOutputs.map((output) => (
                    <code
                      key={output.name}
                      className="text-xs bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-700 px-2 py-1 rounded text-purple-700 dark:text-purple-300"
                    >
                      {`<<${output.name}:${output.type}>>`}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Output Naming Conflicts Error */}
          {outputConflicts.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-900 dark:text-red-200">
                  Output Naming Conflicts
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                  These dynamic outputs conflict with standard processor outputs. Please choose different names:
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {outputConflicts.map((output) => (
                    <code
                      key={output.name}
                      className="text-xs bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 px-2 py-1 rounded text-red-700 dark:text-red-300"
                    >
                      {`<<${output.name}>>`}
                    </code>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Prompt Editor */}
          <div>
            <div className="flex items-start justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Prompt Content
              </label>
              <div className="text-xs text-gray-500 dark:text-gray-400 text-right ml-4">
                <div><code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'{{variable}}'}</code> = content</div>
                <div><code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'[[variable]]'}</code> = reference</div>
                <div><code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'<<variable>>'}</code> = output</div>
              </div>
            </div>
            <textarea
              value={content}
              onChange={(e) => handleContentChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="w-full h-96 p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Enter prompt content...&#10;&#10;Use {{variable}} to insert full content&#10;Use [[variable]] to insert artifact reference (for large files/data)&#10;Use <<variable>> to declare a custom output"
            />
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              <strong>Tip:</strong> Use <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'[[variable]]'}</code> for large files or CSV data to pass metadata instead of full content. Use <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'<<variable>>'}</code> or <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{'<<variable:type>>'}</code> to declare custom outputs that will appear as connectable outputs on this processor.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={handleResetToDefault}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            disabled={content === promptData.content}
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Default
          </button>
          
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

